<template>
  <div>
    <LoadingSpinner :isLoading="isLoading" />
    <modal name="replace-device" class="modal-inner modal-replace-device" transition="pop-out" :width="modalWidth" :focus-trap="true" :min-height="600" height="auto" :scrollable="true" :resizable="true" :adaptive="true" :reset="true" @before-close="resetConfirmation">
      <span class="close-button" @click="hide()"><BaseIcon icon="times-circle" class="text-white" /></span>
      <div class="modal-header text-lg bg-primary-50 font-serif">
        <span v-if="alarmInfo">{{ $t("replace_device.title") }} &mdash; {{ alarmInfo.unit_name }}</span>
      </div>
      <div class="modal-body">
        <!-- What pressing save is going to do, in one sentence. The old device is
             changed by this form too, and that is not obvious from the fields. -->
        <p class="px-2 pb-4 text-xs italic text-gray-600">{{ $t("replace_device.intro") }}</p>
        <ValidationObserver ref="formReplaceDevice" v-if="form != null">
          <!-- SEC-336: where the REPLACED device goes. Defaults to the RMA
               department, which is where these normally belong, but an admin
               can send it somewhere else without leaving the form. -->
          <div class="flex flex-wrap bg-gray-100 rounded mx-2 p-3 mb-5">
            <div class="w-full mb-2">
              <span class="text-sm font-bold text-gray-700 font-serif">{{ $t("replace_device.old_device_goes_to") }}</span>
              <p class="text-xs text-gray-600 mt-1">{{ $t("replace_device.old_device_goes_to_hint") }}</p>
            </div>
            <div class="w-full md:w-1/2 px-2">
              <BaseSelect
                v-model="rma_customer_id"
                :options="customers"
                optionSelector="_id"
                nameSelector="name"
                :field_name="$t('replace_device.old_device_customer')"
                rules="required"
                @change="onRmaCustomerChange()"
              />
            </div>
            <div class="w-full md:w-1/2 px-2">
              <ValidationProvider name="RMA" rules="required" v-slot="{ classes, errors }">
                <div class="input-validate" :class="classes">
                  <label class="text-gray-700 text-sm font-bold pb-2 font-serif">{{ $t("replace_device.old_device_department") }}<b class="text-red-500 ml-1">*</b></label>
                  <select v-model="rma_unit_id" class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-4 border-gray-300 transition duration-500 px-3 pt-2 pb-2 hover:border-gray-400">
                    <option value="">{{ $t("alarm_view.select_business") }}</option>
                    <option :value="u['unit_id']" v-for="(u, i) in rma_customer_units" :key="i">{{ u["name"] }}</option>
                  </select>
                  <span>{{ errors[0] }}</span>
                  <p class="text-xs text-yellow-700 mt-1" v-if="showRmaDepartmentHint">
                    {{ rmaDepartmentName
                        ? $t("replace_device.rma_department_missing", { department: rmaDepartmentName })
                        : $t("replace_device.rma_department_unknown", { type: form.type }) }}
                  </p>
                </div>
              </ValidationProvider>
            </div>
          </div>

          <div class="flex flex-wrap">
            <div class="w-full md:w-1/3 px-2">
              <BaseSelect v-model="form.type" :options="devices" :field_name="$t('change_alarm_plan.device_type')" rules="required" @change="onTypeChange()" />
            </div>
            <!-- Stock delivery document (inleveransdokument), as on the create
                 page: picking one fills in the new hardware's serial and IMEI.
                 Optional; the document is consumed when the replacement is saved. -->
            <div class="w-full md:w-1/3 px-2">
              <BaseSelect v-model="stock_order_id" :options="ordersFiltered" nameSelector="modelnumber" optionSelector="_id" :field_name="$t('alarm_create.delivery_document')" @change="populateFromStockOrder()" />
            </div>
            <!-- SEC-364 A13 / SEC-373: the inventory number sits in the top right cell,
                 as in the alarm action report. All alarm centrals. -->
            <div class="w-full md:w-1/3 px-2" v-if="!new RegExp('(SRT334)|(TWIG Beacon)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(form.type)">
              <BaseInput v-model.trim="form.inventory_number" type="text" :field_name="$t('change_alarm_plan.inventory_number')" />
            </div>
          </div>
          <div class="flex flex-wrap">
            <!-- the IMEI opens the row of hardware identifiers -->
            <div class="w-full md:w-1/3 px-2" v-if="!new RegExp('(SRT334)|(TWIG Beacon)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(form.type)">
              <BaseInput v-model.trim="form.imei_number" type="text" :field_name="$t('change_alarm_plan.imei')" rules="required" />
              <p class="text-xs text-gray-500 mt-1">{{ $t("replace_device.imei_hint") }}</p>
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="!new RegExp('(SRT334)|(TWIG Beacon)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX (?!Hub))').test(form.type)">
              <BaseInput v-model.trim="form.phone" type="text" :field_name="$t('change_alarm_plan.telematics_number')" rules="required" />
              <p class="text-xs text-gray-500 mt-1">{{ $t("replace_device.moved_from_old_device") }}</p>
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="!new RegExp('(Avigilon Alta Compact Dome 5MP)').test(form.type)">
              <BaseInput v-model.trim="form.modelnumber" type="text" :field_name="$t('change_alarm_plan.serial_number')" rules="required" />
              <p class="text-xs text-gray-500 mt-1">{{ $t("replace_device.serial_number_hint") }}</p>
            </div>
            <!-- A camera has no serial-number or IMEI field of its own: its tag
                 serial number is what identifies it, so that is the field the
                 admin fills in for a replacement camera. Without this the form
                 has no mandatory identifier at all for this type. -->
            <div class="w-full md:w-1/3 px-2" v-if="isAvigilonCamera">
              <BaseInput v-model.trim="form.ava_flex.tag_serial_number" type="text" :field_name="$t('alarm_create.tag_serial_number')" rules="required" />
              <p class="text-xs text-gray-500 mt-1">{{ $t("replace_device.serial_number_hint") }}</p>
            </div>

            <div class="w-full md:w-1/3 px-2" v-if="!new RegExp('(SRT334)|(SRT330)|(SRT430)|(TWIG Beacon)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX (?!Hub))').test(form.type)">
              <BaseSelect v-model="form.operator" :options="sim_operators" :field_name="$t('change_alarm_plan.sim_operator')" rules="required" />
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5"></div>

          <div class="flex flex-wrap" v-if="!new RegExp('(SRT334)|(SRT330)|(SRT430)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(form.type)">
            <div class="w-full md:w-1/3 px-2">
              <BaseSelect v-model="form.alarm_central" :options="[{ name: $t('change_alarm_plan.none'), _id: '-' }, ...alarmCentrals]" nameSelector="name" optionSelector="_id" :field_name="$t('change_alarm_plan.alarm_center')" rules="required" @change="onAlarmCentralChange()" />
              <!-- SEC-337: IMSI number, under the alarm centre as on the edit form.
                   Only for Securitas Sverige AB. It belongs to the SIM card, so
                   like the telematics number it moves to the replacement. -->
              <div class="mt-2" v-if="showImsiNumber">
                <BaseInput v-model.trim="form.imsi_number" type="text" :field_name="$t('change_alarm_plan.imsi_number')" />
                <p class="text-xs text-gray-500 mt-1">{{ $t("replace_device.moved_from_old_device") }}</p>
              </div>
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="show_alarm_number">
              <label class="text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t('change_alarm_plan.alarm_number') }}<b class="text-red-500 ml-1">*</b></label>
              <div class="flex">
                <input 
                  v-model.trim="form.alarm_number" 
                  type="text" 
                  class="bg-gray-100 text-sm text-gray-900 focus:outline-none border-b-4 border-gray-300 hover:border-gray-400 transition duration-500 px-3 pt-2 pb-2 disabled:opacity-50 flex-grow rounded-l"
                />
                <button 
                  v-if="isNorwegianSite"
                  type="button"
                  @click="regenerateAlarmNumberFromSerial"
                  class="px-3 bg-primary-500 hover:bg-primary-600 text-white rounded-r border-b-4 border-primary-600 transition-colors"
                  :title="$t('change_alarm_plan.regenerate_alarm_number')"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
            </div>
            <div class="w-full md:w-1/3 px-2">
              <BaseSwitch v-model="form.man_down" :field_name="$t('change_alarm_plan.man_down')" />
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="showDirectDispatch">
              <BaseSwitch v-model="form.direct_dispatch" :field_name="$t('change_alarm_plan.direct_dispatch')" />
            </div>
            <!-- SEC-339: the security company is entered only under "Extern åtgörare"
                 below, which is the one the PDF prints -->
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5" v-if="!new RegExp('(SRT334)|(SRT330)|(SRT430)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(form.type)"></div>

          <!-- SEC-364 A14 / SEC-373: one row container, so cash agreement, unit status and
               the agreement fields fill two rows instead of three, as in the alarm action
               report. All alarm centrals. -->
          <div class="flex flex-wrap mb-5">
            <div class="w-full md:w-1/3 px-2">
              <label class="inline-flex items-center mt-3">
                <input v-model="form.cash_agreement" :value="form.cash_agreement" type="checkbox" class="form-checkbox h-4 w-4" @change="switchAgreementType()" />
                <span class="ml-2 text-sm font-sans font-semibold">{{ $t("change_alarm_plan.cash_agreement") }}</span>
              </label>
            </div>
            <div class="w-full md:w-1/3 px-2">
              <label class="text-gray-600 text-sm font-bold pb-2 font-serif block">{{ $t('device_status.label') }}</label>
              <select v-model="form.device_status" class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-4 border-gray-300 transition duration-500 px-3 pt-2 pb-2 hover:border-gray-400">
                <option value="ny_bestallning">{{ $t('device_status.ny_bestallning') }}</option>
                <option value="utbytesenhet">{{ $t('device_status.utbytesenhet') }}</option>
                <option value="ersattningsprodukt">{{ $t('device_status.ersattningsprodukt') }}</option>
              </select>
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="form.cash_agreement">
              <BaseInput v-model="form.agreement_number" type="text" :field_name="$t('change_alarm_plan.agreement_number')" readonly disabled />
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="form.cash_agreement">
              <BaseInput v-model.trim="form.agreement_length" type="text" :field_name="$t('change_alarm_plan.contract_length')" readonly disabled />
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="!form.cash_agreement">
              <BaseInput v-model.trim="form.agreement_number" type="text" :field_name="$t('change_alarm_plan.agreement_number')" rules="required" readonly disabled />
              <p class="text-xs text-gray-500 mt-1">{{ $t("replace_device.inherited_agreement_number") }}</p>
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="!form.cash_agreement">
              <BaseInput v-model.trim="form.agreement_length" type="text" :field_name="$t('change_alarm_plan.contract_length')" rules="required" />
            </div>
            <div class="w-full md:w-1/3 px-2">
              <ValidationProvider name="Startdatum" rules="required" v-slot="{ classes, errors }">
                <div class="input-validate" :class="classes">
                  <label class="text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t("change_alarm_plan.start_date") }}<b class="text-red-500 ml-1">*</b></label>
                  <date-picker class="mt-2" :lang="datePickerLocale" :key="date_picker_langKey" v-model="form.contract_start" value-type="format" format="YYYY-MM-DD"></date-picker>
                  <span>{{ errors[0] }}</span>
                </div>
              </ValidationProvider>
            </div>
            <div class="w-full md:w-1/3 px-2">
              <BaseSelect v-model="form.seller" :options="sellers" nameSelector="name" optionSelector="_id" :field_name="$t('change_alarm_plan.seller')" @change="check_contract_party()" />
            </div>
            <!-- SEC-321: Avtalspart (contract party) — only for Westra + Commuter Security Group AB (CSG) -->
            <div class="w-full md:w-1/3 px-2" v-if="showContractParty">
              <BaseSelect v-model="contractPartyChoice" :options="[{ name: 'Commuter Security Group AB (CSG)', value: 'Commuter Security Group AB (CSG)' }, { name: $t('change_alarm_plan.none'), value: 'none' }]" nameSelector="name" optionSelector="value" :field_name="$t('change_alarm_plan.avtalspart')" />
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5"></div>

          <div class="flex flex-wrap">
            <div class="w-full md:w-1/3 px-2">
              <BaseInput v-model="form.delivery_company.name" type="text" :field_name="$t('change_alarm_plan.business')" readonly disabled />
            </div>
            <div class="w-full md:w-1/3 px-2">
              <BaseInput v-model="form.delivery_company.phone" type="text" :field_name="$t('change_alarm_plan.phone')" readonly disabled />
            </div>
            <div class="w-full md:w-1/3 px-2">
              <BaseInput v-model="form.delivery_company.email" type="text" :field_name="$t('change_alarm_plan.email')" readonly disabled />
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5"></div>

          <div class="flex flex-wrap" v-if="!new RegExp('(SRT334)|(SRT330)|(SRT430)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(form.type)">
            <div class="w-full md:w-1/3 px-2">
              <BaseSelect v-model="form.selected_alarm_sort" :options="safe4AlarmSortChoices(alarmSortOptions, isSafe4Selected)" nameSelector="label" optionSelector="value" :field_name="$t('change_alarm_plan.alarm_type')" rules="required" @change="check_personal_alarm()" />
            </div>
            <div class="w-full md:w-1/3 px-2">
              <BaseSelect v-model="form.risk_profile" :options="riskProfileOptions" nameSelector="label" optionSelector="value" rules="required" :field_name="$t('change_alarm_plan.risk_profile')" v-if="!show_MSL_risk"/>
              <BaseSelect v-model="form.risk_profile" :options="riskProfileOptionsMSL" nameSelector="label" optionSelector="value" rules="required" :field_name="$t('change_alarm_plan.risk_profile')" v-if="show_MSL_risk"/>
            </div>
            <div class="w-full md:w-1/3 px-2">
              <BaseInput v-model="form.user" type="number" rules="required" :field_name="$t('change_alarm_plan.number_of_users')" />
            </div>
          </div>

          <div class="flex flex-wrap" v-if="isBurglarAlarm || isThreatAlarm">
            <div class="w-full md:w-1/3 px-2">
              <BaseInput v-model="form.reference_number" type="text" :field_name="$t('change_alarm_plan.reference_number')" />
            </div>
          </div>

          <div class="flex flex-wrap" v-if="show_personal_alarm && !isSafe4Selected && !new RegExp('(SRT334)|(SRT330)|(SRT430)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(form.type)">
            <div class="w-full md:w-1/3 px-2">
              <BaseInput v-model="form.personal_alarm.reporting" type="text" :field_name="$t('change_alarm_plan.signal_element')" rules="required" />
            </div>
            <div class="w-full md:w-1/3 px-2">
              <BaseInput v-model="form.personal_alarm.name" type="text" :field_name="$t('change_alarm_plan.name')" rules="required" />
            </div>
            <div class="w-full md:w-1/3 px-2">
              <BaseInput v-model="form.personal_alarm.home_address" type="text" :field_name="$t('change_alarm_plan.home_address')" rules="required" />
            </div>
            <div class="w-full md:w-1/3 px-2">
              <BaseSelect v-model="form.personal_alarm.transport" :options="transportOptions" nameSelector="label" optionSelector="value" :field_name="$t('change_alarm_plan.transport')" />
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="form.personal_alarm.transport == 'car'">
              <BaseInput v-model="form.personal_alarm.registration_number" type="text" :field_name="$t('change_alarm_plan.registration_number')" rules="required" />
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="form.personal_alarm.transport == 'car'">
              <BaseInput v-model="form.personal_alarm.model" type="text" :field_name="$t('change_alarm_plan.model')" rules="required" />
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="form.personal_alarm.transport == 'car'">
              <BaseInput v-model="form.personal_alarm.color" type="text" :field_name="$t('change_alarm_plan.color')" rules="required" />
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5" v-if="!new RegExp('(SRT334)|(SRT330)|(SRT430)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(form.type)"></div>

          <div class="flex flex-wrap">
            <div class="w-full md:w-1/3 px-2">
              <BaseInput v-model="form.unit_name" type="text" rules="required" :field_name="$t('change_alarm_plan.product_name')" />
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="!isSafe4Selected && !new RegExp('(SRT334)|(SRT330)|(SRT430)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(form.type)">
              <BaseInput v-model="form.from_address.city" type="text" rules="required" :field_name="$t('change_alarm_plan.based_on_address')" />
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="!new RegExp('(SRT334)|(SRT330)|(SRT430)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(form.type)">
              <BaseInput v-model="form.alarm_unit_name" type="text" rules="required" :field_name="$t('change_alarm_plan.business_name')" />
            </div>
            <!-- Safe-4 (SEC-364 A12): street, postal code and city belong together,
                 so they get a row of their own instead of wrapping across two.
                 Splitting them also keeps the KUIS postkode a clean value (req 8). -->
            <template v-if="isSafe4Selected">
              <div class="w-full flex flex-wrap">
                <div class="w-full md:w-1/3 px-2">
                  <BaseInput v-model="form.from_address.street" type="text" rules="required|max:30" maxlength="30" :field_name="$t('safe4_form.street')" />
                </div>
                <div class="w-full md:w-1/3 px-2">
                  <BaseInput v-model="form.from_address.zip" type="text" :rules="safe4PostcodeRules" :field_name="$t('safe4_form.postal_code')" />
                </div>
                <div class="w-full md:w-1/3 px-2">
                  <BaseInput v-model="form.from_address.city" type="text" rules="required" :field_name="$t('safe4_form.city')" />
                </div>
              </div>
            </template>
            <div class="w-full md:w-1/3 px-2" v-if="!new RegExp('(SRT334)|(SRT330)|(SRT430)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(form.type)">
              <BaseTextarea rows="5" v-model="form.business_description" :field_name="$t('change_alarm_plan.work_assignment')" rules="required" />
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="form.type == 'SRT330' || form.type == 'SRT430'">
              <BaseTextarea rows="5" v-model="form.comment" :field_name="$t('change_alarm_plan.comment')" />
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5" v-if="!new RegExp('(SRT334)|(SRT330)|(SRT430)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(form.type)"></div>

          <div class="flex flex-wrap" v-if="!new RegExp('(SRT334)|(SRT330)|(SRT430)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(form.type)">
            <div class="w-full text-xs italic px-2 mb-2">
              {{ isSosAlarm ? $t("change_alarm_plan.device_password_sos") : $t("change_alarm_plan.device_password") }}
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="!new RegExp('(SRT334)|(SRT330)|(SRT430)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(form.type)">
              <BaseInput v-model="form.alarm_password" vid="alarm-password" type="text" :rules="isSafe4Selected ? 'required|max:20|safe4_password' : 'required'" :field_name="isSosAlarm ? $t('change_alarm_plan.password_sos') : $t('change_alarm_plan.password')" :maxlength="isSafe4Selected ? 20 : (isSosAlarm ? 10 : undefined)" />
            </div>
            <!-- Safe-4 (SEC-364 A5): KUIS has no personal password, so the field is
                 hidden and its value cleared when Safe-4 is the alarm central. -->
            <div class="w-full md:w-1/3 px-2" v-if="!isSafe4Selected && !new RegExp('(SRT334)|(SRT330)|(SRT430)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(form.type)">
              <BaseInput v-model="form.personal_password" type="text" :field_name="isSosAlarm ? $t('change_alarm_plan.personal_password_sos') : $t('change_alarm_plan.personal_password')" :maxlength="isSosAlarm ? 10 : undefined" />
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5" v-if="!new RegExp('(SRT334)|(SRT330)|(SRT430)|(TWIG Beacon)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(form.type)"></div>

          <div class="flex flex-wrap" v-if="!new RegExp('(SRT334)|(SRT330)|(SRT430)|(TWIG Beacon)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(form.type)">
            <div class="w-full text-xs italic px-2 mb-2">
              {{ $t("change_alarm_plan.external_agent") }}
            </div>
            <div class="w-full md:w-1/3 px-2">
              <BaseInput v-model="form.external_contractor.name" type="text" :field_name="$t('change_alarm_plan.name')" />
            </div>
            <div class="w-full md:w-1/3 px-2">
              <BaseInput v-model="form.external_contractor.phone" type="text" :field_name="$t('change_alarm_plan.contact_number')" />
            </div>
            <div class="w-full md:w-1/3 px-2">
              <BaseInput v-model="form.external_contractor.object_id" type="text" :field_name="$t('change_alarm_plan.id_perpetrator')" />
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5" v-if="!new RegExp('(SRT334)|(SRT330)|(SRT430)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(form.type)"></div>

          <div class="flex flex-wrap" v-if="!new RegExp('(SRT334)|(SRT330)|(SRT430)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(form.type)">
            <div class="w-full text-sm font-bold px-2 mb-2">
              {{ $t("change_alarm_plan.contact_person") }}
            </div>
            <div class="w-full text-xs italic px-2 mb-2">
              <p>{{ $t("change_alarm_plan.contact_person_details") }}</p>

              <ol>
                <li>{{ $t("change_alarm_plan.wardenship") }} – 031 703 10 00 - {{ $t("change_alarm_plan.everyday") }} 08-16</li>
                <li>{{ $t("change_alarm_plan.palm_leather") }} – 0799 99 99 99 - {{ $t("change_alarm_plan.around_the_clock") }}</li>
              </ol>
            </div>
            <!-- Safe-4 (SEC-364 A4): the contact persons are called around the clock,
                 so the time fields hold the whole day. The note sits above them, in italics. -->
            <div class="w-full px-2 text-xs italic mb-1" v-if="isSafe4Selected">
              {{ $t("safe4_form.around_clock") }}
            </div>
            <div class="w-full flex flex-wrap" v-if="!new RegExp('(SRT334)|(SRT330)|(SRT430)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(form.type)">
              <div class="w-full md:w-3/12 px-2">
                <BaseInput v-model="form.contact_persons[0].name" type="text" :rules="isSafe4Selected ? 'required|max:50' : 'required'" :maxlength="isSafe4Selected ? 50 : undefined" :field_name="$t('change_alarm_plan.name')" :placeholder="$t('change_alarm_plan.name')" />
              </div>
              <div class="w-full md:w-3/12 px-2">
                <BaseInput v-model="form.contact_persons[0].function" type="text" :rules="isSafe4Selected ? 'required|max:40' : 'required'" :maxlength="isSafe4Selected ? 40 : undefined" :field_name="$t('change_alarm_plan.function')" :placeholder="$t('change_alarm_plan.function')" />
              </div>
              <div class="w-full md:w-3/12 px-2">
                <BasePhoneInput v-model="form.contact_persons[0].phone" vid="contact-phone-0" :show_country="isSafe4Selected" :rules="safe4PhoneRules(form.contact_persons, 0, isSafe4Selected, true)" :maxlength="isSafe4Selected ? 20 : undefined" :field_name="$t('change_alarm_plan.number')" :placeholder="$t('change_alarm_plan.number')" />
              </div>
              <div class="w-full md:w-1/12 px-2">
                <BaseInput v-model="form.contact_persons[0].weekday" type="text" :disabled="isSafe4Selected" :field_name="$t('change_alarm_plan.everyday')" :placeholder="$t('change_alarm_plan.everyday')" />
              </div>
              <div class="w-full md:w-1/12 px-2">
                <BaseInput v-model="form.contact_persons[0].saturday" type="text" :disabled="isSafe4Selected" :field_name="$t('change_alarm_plan.saturday')" :placeholder="$t('change_alarm_plan.saturday')" />
              </div>
              <div class="w-full md:w-1/12 px-2">
                <BaseInput v-model="form.contact_persons[0].sunday" type="text" :disabled="isSafe4Selected" :field_name="$t('change_alarm_plan.sun_weekend')" :placeholder="$t('change_alarm_plan.sun_weekend')" />
              </div>
            </div>
            <div class="w-full" v-for="(c, i) in form.contact_persons" :key="i">
              <div class="flex flex-wrap" v-if="i > 0">
                <div class="w-full md:w-3/12 px-2">
                  <BaseInput v-model="c.name" type="text" :rules="isSafe4Selected ? 'max:50' : ''" :maxlength="isSafe4Selected ? 50 : undefined" :placeholder="$t('change_alarm_plan.name')" />
                </div>
                <div class="w-full md:w-3/12 px-2">
                  <BaseInput v-model="c.function" type="text" :rules="isSafe4Selected ? 'max:40' : ''" :maxlength="isSafe4Selected ? 40 : undefined" :placeholder="$t('change_alarm_plan.function')" />
                </div>
                <div class="w-full md:w-3/12 px-2">
                  <BasePhoneInput v-model="c.phone" :vid="'contact-phone-' + i" :show_country="isSafe4Selected" :rules="safe4PhoneRules(form.contact_persons, i, isSafe4Selected, false)" :maxlength="isSafe4Selected ? 20 : undefined" :placeholder="$t('change_alarm_plan.number')" />
                </div>
                <div class="w-full md:w-1/12 px-2">
                  <BaseInput v-model="c.weekday" type="text" :disabled="isSafe4Selected" :placeholder="$t('change_alarm_plan.everyday')" />
                </div>
                <div class="w-full md:w-1/12 px-2">
                  <BaseInput v-model="c.saturday" type="text" :disabled="isSafe4Selected" :placeholder="$t('change_alarm_plan.saturday')" />
                </div>
                <div class="w-full md:w-1/12 px-2">
                  <BaseInput v-model="c.sunday" type="text" :disabled="isSafe4Selected" :placeholder="$t('change_alarm_plan.sun_weekend')" />
                </div>
              </div>
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5" v-if="!new RegExp('(SRT334)|(SRT330)|(SRT430)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(form.type)"></div>

          <div class="flex flex-wrap mt-4" v-if="!new RegExp('(SRT334)|(SRT330)|(SRT430)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(form.type)">
            <!-- Safe-4 (SEC-364 A1 / SEC-373): the operator instructions sit in a
                 collapsed section, as in the alarm action report. Click the header
                 to open them. Other alarm centrals keep them open, as before. -->
            <div class="w-full px-2" v-if="isSafe4Selected">
              <div class="flex items-center cursor-pointer text-sm font-semibold py-2" @click="show_operator_instructions = !show_operator_instructions">
                <span class="mr-2">{{ $t("change_alarm_plan.operator_instructions") }}</span>
                <BaseIcon icon="angle-down" v-if="!show_operator_instructions" />
                <BaseIcon icon="angle-up" v-else />
              </div>
            </div>
            <template v-if="!isSafe4Selected || show_operator_instructions">
            <div class="w-full px-2 text-xs italic" v-if="isThreatAlarm">
              <h5 class="font-bold mb-1">{{ $t("primary_input.primary_input") }}</h5>
              <p>
                <span class="font-bold">{{ $t("primary_input.answer_mic") }}</span> {{ $t("primary_input.answer_with_mic") }}
              </p>
              <h5 class="font-bold mb-1 mt-2">{{ $t("alarm_action.action_in_alarm") }}</h5>
              <ol>
                <li>
                  <span class="font-bold">{{ $t("alarm_action.co_listening") }}</span> {{ $t("alarm_action.confirm_alarm_type") }}
                </li>
                <li>
                  <span class="font-bold">{{ $t("alarm_action.summon") }}</span> {{ $t("alarm_action.police_and_external_agent") }}
                </li>
                <li>
                  <span class="font-bold">{{ $t("alarm_action.emergancy_event") }}</span
                  >{{ $t("alarm_action.communicate") }}
                </li>
                <li>{{ $t("alarm_action.options") }}</li>
                <li>
                  <span class="font-bold">{{ $t("alarm_action.contact_person") }}</span> {{ $t("alarm_action.notify_sharp_alarm") }}
                </li>
                <li>
                  <span class="font-bold">{{ $t("alarm_action.recall") }}</span
                  >{{ $t("alarm_action.alarm_authorization") }}
                </li>
              </ol>
            </div>

            <div class="w-full px-2 text-xs italic" v-if="isEmergencyAlarm">
              <h5 class="font-bold mb-1">{{ $t("primary_input_mic_on.primary_input") }}</h5>
              <p>
                <span class="font-bold">{{ $t("primary_input_mic_on.primary_input") }}</span> {{ $t("primary_input_mic_on.answer_with_mic") }}
              </p>
              <h5 class="font-bold mb-1 mt-2">{{ $t("alarm_action.action_in_alarm") }}</h5>
              <ol>
                <li>
                  <span class="font-bold">{{ $t("alarm_action.co_listening") }}</span> {{ $t("alarm_action.confirm_alarm_type") }}
                </li>
                <li>
                  <span class="font-bold">{{ $t("communicate.communicate") }}</span> {{ $t("communicate.talk_to_persion") }}
                </li>
                <li>
                  <span class="font-bold">{{ $t("alarm_action.contact_person") }}</span
                  >{{ $t("alarm_action.notify_sharp_alarm2") }}
                </li>
                <li>
                  <span class="font-bold">{{ $t("alarm_action.recall") }} </span>{{ $t("alarm_action.alarm_authorization") }}
                </li>
              </ol>
            </div>

            <div class="w-full px-2 text-xs italic" v-if="isBurglarAlarm">
              <h5 class="font-bold mb-1">{{ $t("primary_input.primary_input") }}</h5>
              <p>
                {{ $t("risk_profile.burglar_alarm") }}
              </p>
              <h5 class="font-bold mb-1 mt-2">{{ $t("alarm_action.action_in_alarm") }}</h5>
              <ol>
                <li>
                  <span class="font-bold">{{ $t("alarm_action.summon") }}</span> {{ $t("info.info_1") }}
                </li>
                <li>
                  <span class="font-bold">{{ $t("alarm_action.co_listening") }}</span> {{ $t("info.info_2") }}
                </li>
                <li>
                  <span class="font-bold">{{ $t("alarm_action.contact_person") }}</span> {{ $t("info.info_3") }}
                </li>
                <li>
                  <span class="font-bold">{{ $t("alarm_action.recall") }}</span> {{ $t("info.info_4") }}
                </li>
              </ol>
            </div>
            </template>
          </div>
        </ValidationObserver>
      </div>
      <div class="modal-footer">
        <!-- SEC-336: three buttons, one of them with a long label, so this row
             is laid out with flex rather than floats. With floats the widest
             button pushes the next one onto a second line, and the footer is
             pinned to the bottom of a height:auto modal - so that second line
             renders outside the modal box and the button simply disappears. -->
        <div class="footer-container flex flex-wrap items-center justify-between">
          <button class="btn-red-outline px-3 py-2 whitespace-nowrap" @click="hide()">{{ $t("user_actions.cancel") }}<BaseIcon icon="times-circle" class="ml-1" /></button>
          <div class="flex flex-wrap items-center">
            <!-- Two ways to finish. Both ask for the case number first; the
                 second one also sends the updated action report to the alarm
                 receiving centre. -->
            <button class="btn-blue px-3 py-2 whitespace-nowrap" :disabled="isLoading" @click="askCaseNumber(false)">{{ $t("replace_device.save") }}<BaseIcon icon="save" class="ml-1" /></button>
            <button class="btn-green px-3 py-2 ml-2 whitespace-nowrap" v-if="canSendToAlarmCenter && !isSafe4Selected" :disabled="isLoading" @click="askCaseNumber(true)">{{ $t("replace_device.save_and_send") }}<BaseIcon icon="paper-plane" class="ml-1" /></button>
          </div>
        </div>
      </div>

    <!-- SEC-336: the confirmation, asked once the form is filled in and valid.
         This is where the replacement is actually agreed to, and where the RMA
         case number is entered. An inline overlay rather than the shared
         dialog plugin, because that one cannot hold an input field. -->
    <div class="fixed inset-0 flex items-center justify-center p-5" style="background: rgba(0, 0, 0, 0.45); z-index: 1100" v-if="askingCaseNumber">
      <div class="bg-white rounded shadow-lg p-5 w-full" style="max-width: 460px">
        <div class="text-base font-bold text-gray-800 font-serif mb-2">{{ $t("replace_device.confirm_title") }}</div>
        <div class="text-xs text-gray-600 mb-4">
          {{ sendToAlarmCenter ? $t("replace_device.confirm_text_send") : $t("replace_device.confirm_text") }}
        </div>
        <label class="text-gray-700 text-sm font-bold pb-2 font-serif">{{ $t("replace_device.case_number") }}<b class="text-red-500 ml-1">*</b></label>
        <input
          v-model.trim="caseNumber"
          type="text"
          class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-4 border-gray-300 transition duration-500 px-3 pt-2 pb-2 hover:border-gray-400 mb-1"
          @keyup.enter="confirmCaseNumber()"
        />
        <div class="text-xs text-red-600 mb-3" v-if="caseNumberError">{{ $t("replace_device.case_number_required") }}</div>
        <div class="text-xs text-gray-500 mb-3" v-else>{{ $t("replace_device.case_number_hint") }}</div>
        <div class="clearfix">
          <button type="button" class="btn-green px-3 py-2 mr-2 mb-1" :disabled="isLoading" @click="confirmCaseNumber()">{{ $t("user_actions.confirm") }}</button>
          <!-- Cancel is deliberately NOT disabled while saving: it only closes
               this box, and leaving it dead would trap the admin behind a
               full-screen overlay if the request ever hangs. -->
          <button type="button" class="btn-red-outline px-3 py-2 mb-1" @click="cancelCaseNumber()">{{ $t("user_actions.cancel") }}</button>
        </div>
      </div>
    </div>
    </modal>
  </div>
</template>

<script>
// SEC-336: the action report for a replacement device.
//
// It shows the same fields as the ordinary action report ("Andra larmplan"),
// filled in from the device being replaced, and it is the point at which the
// whole replacement is actually saved. Up until Save is pressed nothing has
// been written and both devices are untouched.
//
// What starts out different from the device being replaced:
//   - serial number: empty and required. This is the new physical unit, so the
//     admin types its serial number in.
//   - IMEI: empty and required too. The new hardware carries its own IMEI, so
//     it is not inherited - but it IS cleared from the old device when the
//     replacement is saved, so reports stop pointing at the old record.
//   - telematics number: inherited from the old device and cleared from it when
//     the replacement is saved (PM 2026-09-22). The SIM moves across.
//   - inventory number, alarm number, camera fields: emptied, because they
//     belong to the old physical unit.
//   - contract number: inherited and read-only. It moves to the replacement.
//   - device status: "utbytesenhet" - this is an exchange unit (PM 2026-09-22).
//
// Everything else - the notification settings, the tunnel, the alarm central,
// the contact persons - is inherited on the server by copying the old device
// record, so it is not sent from here and cannot be lost on the way.
//
// Saving returns as soon as both devices are written. Safe-4 and Westra are
// updated in the background by the server, so the replacement is never held up
// by, or reported as failed because of, an alarm central being slow or down.
const MODAL_WIDTH = 1200;

// SEC-336: where a replaced device is sent by default.
//
// The customer differs between servers - "Personal Securdia AB" in production,
// "Securdia AB" in staging - so the names are tried in order and the first one
// that exists is used.
const DEFAULT_RMA_CUSTOMER_NAMES = ["Personal Securdia AB", "Personal Securdia", "Securdia Personal", "Securdia AB"];

// Device types that have no action report to send. The ordinary action-report
// form refuses to build a PDF for these (alarm_detail.vue), so the replacement
// must not offer to send one either - it would either email a near-empty
// document or fail while building it.
const TYPES_WITHOUT_ACTION_REPORT = ["SRT330", "SRT430", "SRT334", "TWIG Beacon", "Anchor Beacon"];

// The department is per manufacturer, and the manufacturer is read off the
// device type: every type in the register starts with its maker's name
// ("SRT306", "TWIG Neo", "EMERIT WG100", "AJAX Hub 2 Plus").
//
// Types that match none of these - TELTONIKA, 4G Box, Anchor Beacon, Avigilon,
// SecurDesktop, SecurApp - have no RMA department of their own. Nothing is
// guessed for them: the department is left empty and the admin picks one
// (PM 2026-09-22, "if support do they can create one or place them in a
// generic one").
const RMA_DEPARTMENT_BY_MANUFACTURER = [
  { match: /^SRT/i, department: "RMA SRT" },
  { match: /^TWIG/i, department: "RMA TWIG" },
  { match: /^EMERIT/i, department: "RMA EMERIT" },
  { match: /^AJAX/i, department: "RMA AJAX" },
];

import DatePicker from "vue2-datepicker";
import "vue2-datepicker/locale/sv";
import "vue2-datepicker/locale/pl";
import "vue2-datepicker/locale/nb";
import "@/utils/datePickerDutch";
import "vue2-datepicker/index.css";
import _ from "lodash";
import siteContext from "@/utils/siteContext";
import { RISK_PROFILE, ALARM_SORT, normalizeRiskProfile, normalizeAlarmSort } from "@/utils/alarmEnums";
import { DEVICE_TYPES } from "@/utils/deviceTypes";
import { mergeContactPersons } from "@/utils/alarmDocumentation";
import { deliveryCompanyForSeller } from "@/utils/deliveryCompany";
import { isAgreementNumber } from "@/utils/countryOrigin";

// Safe-4 (SEC-364 A3): contact persons are reached around the clock, so the three
// time fields hold the whole day - stored the way KUIS has it, so SecurCloud
// mirrors SAFECON (Rasmus, Q6: "store it as KUIS have so it mirrors their system"). KUIS keeps
// 00:00:00-23:59:59 on every contact; this is the same window in the fields'
// own HH:MM-HH:MM format (SEC-320).
const SAFE4_CONTACT_TIME = "00:00-23:59";

export default {
  siteContext,
  props: {
    // The device being replaced. Everything on the form starts from this.
    alarmInfo: {
      required: true,
      default: null,
    },
    alarmCentrals: {
      required: true,
    },
    sellers: {
      required: true,
    },
    // Used for the customer name printed on the action report when it is sent
    // to the alarm receiving centre.
    customer: {
      required: false,
      default: null,
    },
  },
  components: {
    DatePicker,
  },
  data() {
    return {
      isLoading: false,
      // The replacement being filled in. Never the old device itself.
      form: null,
      // Safe-4: the AlarmCenter _id that is Safe-4 (empty = integration off)
      safe4_center_id: "",
      show_operator_instructions: false, // SEC-364 A1: collapsed until the header is clicked
      show_alarm_number: false,
      show_MSL_risk: false,
      show_personal_alarm: false,
      // SEC-336: where the replaced device is sent. Defaults to the RMA
      // department under the customer named below.
      customers: [],
      all_units: [],
      rma_customer_units: [],
      rma_customer_id: "",
      rma_unit_id: "",
      // SEC-336: the confirmation step. Nothing is saved until it is confirmed.
      askingCaseNumber: false,
      sendToAlarmCenter: false,
      caseNumber: "",
      caseNumberError: false,
      date_picker_lang: this.$i18n.locale,
      date_picker_langKey: 0,
      // Stock delivery documents (inleveransdokument) to fill the new
      // hardware's identifiers from, and the one picked.
      orders: [],
      stock_order_id: "",
    };
  },

  computed: {
    user() {
      return this.$store.state.user;
    },
    // Delivery documents for the selected device type, as on the create page.
    ordersFiltered() {
      const type = (this.form && this.form.type) || "";
      return type ? this.orders.filter((o) => o.type === type) : this.orders;
    },
    modalWidth() {
      return window.innerWidth < MODAL_WIDTH ? window.innerWidth - 20 : MODAL_WIDTH;
    },
    // The RMA department this device's manufacturer should go to, or "" when
    // the manufacturer has none.
    rmaDepartmentName() {
      const type = (this.form && this.form.type) || "";
      const entry = RMA_DEPARTMENT_BY_MANUFACTURER.find((m) => m.match.test(type));
      return entry ? entry.department : "";
    },
    // Shown under the department picker when nothing could be suggested, so
    // the admin knows why they are being asked to choose.
    showRmaDepartmentHint() {
      return !this.rma_unit_id;
    },
    // Can this device's action report be sent to an alarm receiving centre at
    // all? Some types have no report, and some are not connected to a centre.
    canSendToAlarmCenter() {
      if (!this.form) return false;
      if (TYPES_WITHOUT_ACTION_REPORT.indexOf(this.form.type) > -1) return false;
      const central = this.form.alarm_central;
      return !!central && central !== "-";
    },
    // Cameras identify themselves by their tag serial number, not by the
    // serial-number/IMEI pair the other device types use.
    isAvigilonCamera() {
      return !!this.form && /Avigilon Alta Compact Dome 5MP/.test(this.form.type || "");
    },
    alarmSortOptions() {
      return [
        { value: ALARM_SORT.GROUP, label: this.$t('change_alarm_plan.group_alarm') },
        { value: ALARM_SORT.PERSONAL, label: this.$t('change_alarm_plan.personal_alarm') },
      ];
    },
    transportOptions() {
      return [
        { value: 'walking', label: this.$t('change_alarm_plan.transport_walking') },
        { value: 'bike', label: this.$t('change_alarm_plan.transport_bike') },
        { value: 'public_transport', label: this.$t('change_alarm_plan.transport_public_transport') },
        { value: 'car', label: this.$t('change_alarm_plan.transport_car') },
      ];
    },
    riskProfileOptions() {
      return [
        { value: RISK_PROFILE.THREAT, label: this.$t('risk_profile.threat_alarm') },
        { value: RISK_PROFILE.EMERGENCY, label: this.$t('risk_profile.emergency_alarm') },
        { value: RISK_PROFILE.ASSAULT, label: this.$t('risk_profile.burglar_alarm') },
      ];
    },
    riskProfileOptionsMSL() {
      // Malmö special-case options are intentionally kept as raw label values
      // (handled separately); only the standard profiles use stable codes.
      return [
        ...this.riskProfileOptions,
        { value: 'Malmö Fast', label: 'Malmö Fast' },
        { value: 'Malmö Rörlig', label: 'Malmö Rörlig' },
        { value: 'Malmö nödsändare', label: 'Malmö nödsändare' },
      ];
    },
    devices() {
      // Single source of truth: @/utils/deviceTypes (SEC-216)
      return DEVICE_TYPES;
    },
    // Safe-4 selected in the single-device form?
    isSafe4Selected() {
      return !!this.safe4_center_id && this.form && this.form.alarm_central == this.safe4_center_id;
    },
    // Safe-4 (SEC-370): a Norwegian site's postcode must be inside the county
    // ranges (utils/safe4Postcodes.js) - the server refuses the save otherwise.
    safe4PostcodeRules() {
      const country = (this.form && this.form.country) || (this.$options.siteContext.isNorwegianSite() ? "no" : "se");
      return `required|safe4_postcode:${country}`;
    },
    showDirectDispatch() {
      // Safe-4 (SEC-316): the toggle decides what Safe-4 registers for the
      // device — VEKTERRYKK (guard dispatch) or TELEFONVARSLING (phone only) —
      // so it is always available when the central is Safe-4.
      if (this.isSafe4Selected) return true;
      // Show direct dispatch option only for Norwegian site and if the selected alarm central has guard_dispatch enabled
      if (!this.$options.siteContext.isNorwegianSite()) return false;
      if (!this.form || !this.form.alarm_central || this.form.alarm_central === '-') return false;
      const selectedAlarmCentral = _.find(this.alarmCentrals, { _id: this.form.alarm_central });
      return selectedAlarmCentral && selectedAlarmCentral.guard_dispatch === true;
    },
    isSosAlarm() {
      if (!this.form || !this.form.alarm_central || this.form.alarm_central === '-') return false;
      const selectedAlarmCentral = _.find(this.alarmCentrals, { _id: this.form.alarm_central });
      return selectedAlarmCentral && /^SOS Alarm/i.test(selectedAlarmCentral.name);
    },
    // SEC-321: the "Avtalspart" field shows only for Westra + Commuter Security Group AB (CSG)
    isWestraSelected() {
      if (!this.form || !this.form.alarm_central || this.form.alarm_central === '-') return false;
      const selectedAlarmCentral = _.find(this.alarmCentrals, { _id: this.form.alarm_central });
      return selectedAlarmCentral && /Westra/i.test(selectedAlarmCentral.name);
    },
    isCsgSeller() {
      const selectedSeller = _.find(this.sellers, { _id: this.form && this.form.seller });
      return selectedSeller && selectedSeller.name === 'Commuter Security Group AB (CSG)';
    },
    showContractParty() {
      return this.isWestraSelected && this.isCsgSeller;
    },
    // SEC-337: same rule as the edit form - Securitas Sverige AB only, matched
    // by its exact name so the separate "Securitas AB" centre does not show it.
    showImsiNumber() {
      if (!this.form || !this.form.alarm_central || this.form.alarm_central === '-') return false;
      const selectedAlarmCentral = _.find(this.alarmCentrals, { _id: this.form.alarm_central });
      return !!selectedAlarmCentral && selectedAlarmCentral.name === 'Securitas Sverige AB';
    },
    // SEC-321: proxy the dropdown so the None option has a real value ('none')
    // instead of '' (BaseSelect already renders a value="" placeholder, which
    // would otherwise shadow it). The stored contract_party stays "" for None.
    contractPartyChoice: {
      get() {
        return (this.form && this.form.contract_party) || 'none';
      },
      set(v) {
        if (this.form) this.form.contract_party = v === 'none' ? '' : v;
      },
    },
    isNorwegianSite() {
      return this.$options.siteContext.isNorwegianSite();
    },
    datePickerLocale() {
      // Map 'no' to 'nb' for date picker (Norwegian Bokmål)
      return this.date_picker_lang === 'no' ? 'nb' : this.date_picker_lang;
    },
    // Check risk profile against all possible translations
    isThreatAlarm() {
      if (!this.form) return false;
      return normalizeRiskProfile(this.form.risk_profile) === RISK_PROFILE.THREAT;
    },
    isEmergencyAlarm() {
      if (!this.form) return false;
      return normalizeRiskProfile(this.form.risk_profile) === RISK_PROFILE.EMERGENCY;
    },
    isBurglarAlarm() {
      if (!this.form) return false;
      return normalizeRiskProfile(this.form.risk_profile) === RISK_PROFILE.ASSAULT;
    },
    sim_operators() {
      return ["Telia", "Telia DCP", "Telenor", "Tele2", this.$t('change_alarm_plan.customer_sim_card')];
    },
  },

  watch: {
    // Safe-4 (SEC-364 A3/A5/A16): picking Safe-4 fills the contact time fields
    // with the whole day, empties the personal password and turns a personal alarm into
    // a group alarm - the same as the alarm action report. The server applies
    // the same rules on save, so this is what the user sees before saving.
    isSafe4Selected(isSafe4) {
      if (isSafe4) this.applySafe4FieldDefaults();
    },
    // SEC-373: a Safe-4 device replaced right after another one - the central
    // stays Safe-4, so the watcher above does not fire - gets the defaults too,
    // so its locked time fields show the whole day and not what it had stored
    "form.contact_persons"() {
      if (this.isSafe4Selected) this.applySafe4FieldDefaults();
    },
    alarmInfo(newVal) {
      this.show_operator_instructions = false; // every device opens collapsed (SEC-373)
      this.prefillFromDevice(newVal);
      // The suggested department depends on the device's manufacturer, so it
      // can only be worked out once the device is known.
      if (this.customers.length) this.preselectRmaDepartment();
    },
    alarmCentrals() {
      this.check_alarm_center_specifics();
    },
    // The "Företag" fields follow the seller (see syncDeliveryCompany)
    "form.seller"() {
      this.syncDeliveryCompany();
    },
    sellers() {
      this.syncDeliveryCompany();
    },
    "$i18n.locale"(newLocale) {
      this.date_picker_lang = newLocale;
      this.date_picker_langKey++;
    },
  },

  created() {
    this.getSafe4CenterId();
    this.getCustomersAndUnits();
    this.getStockOrders();
    if (this.alarmInfo) this.prefillFromDevice(this.alarmInfo);
  },

  methods: {
    // The Safe-4 defaults for the replacement (SEC-364), as in the action report
    // Upper-case the fields KUIS keeps in upper case (SEC-364): the password,
    // every contact person's name and function, and the street. Run just before
    // sending, so typing stays normal - as in the action report and create form.
    applySafe4UpperCase() {
      if (!this.form) return;
      const upper = (value) => String(value == null ? "" : value).trim().toUpperCase();
      this.$set(this.form, "alarm_password", upper(this.form.alarm_password));
      (this.form.contact_persons || []).forEach((contact) => {
        this.$set(contact, "name", upper(contact.name));
        this.$set(contact, "function", upper(contact.function));
      });
      if (this.form.from_address) this.$set(this.form.from_address, "street", upper(this.form.from_address.street));
    },
    applySafe4FieldDefaults() {
      if (!this.form) return;

      (this.form.contact_persons || []).forEach((contact) => {
        this.$set(contact, "weekday", SAFE4_CONTACT_TIME);
        this.$set(contact, "saturday", SAFE4_CONTACT_TIME);
        this.$set(contact, "sunday", SAFE4_CONTACT_TIME);
      });

      this.$set(this.form, "personal_password", "");

      if (normalizeAlarmSort(this.form.selected_alarm_sort) === ALARM_SORT.PERSONAL) {
        this.$set(this.form, "selected_alarm_sort", ALARM_SORT.GROUP);
        this.show_personal_alarm = false;
      }
    },
    // The "Företag" fields: the reseller's name, phone and e-mail when a reseller sold the
    // device, Securdia for the device's country otherwise. A seller missing from the list
    // (not loaded yet) leaves the stored values alone.
    syncDeliveryCompany() {
      if (!this.form) return;
      const country = this.form.country || (this.$options.siteContext.isNorwegianSite() ? "no" : "se");
      const company = deliveryCompanyForSeller(this.sellers, this.form.seller, country);
      if (company) this.form.delivery_company = company;
    },
    // Fill the form in from the device being replaced.
    //
    // The first half is the same normalisation the ordinary action report does
    // when it opens a device: old records were saved before some of these
    // fields existed, or saved with translated labels instead of codes, and the
    // dropdowns and the template need them in a known shape.
    //
    // The second half is what makes this a REPLACEMENT rather than a copy.
    prefillFromDevice(device) {
      if (!device) {
        this.form = null;
        return;
      }

      this.form = _.cloneDeep(device);

      // Legacy translated labels back to canonical codes, so the dropdowns
      // select correctly whichever language the device was saved in.
      this.form.risk_profile = normalizeRiskProfile(this.form.risk_profile);
      this.form.selected_alarm_sort = normalizeAlarmSort(this.form.selected_alarm_sort);

      // Fields that may be missing on older devices, made reactive.
      this.$set(this.form, "reference_number", this.form.reference_number || "");
      this.$set(this.form, "contract_party", this.form.contract_party || "");
      this.$set(this.form, "imsi_number", this.form.imsi_number || ""); // SEC-337

      // "Based on address" is street / zip / city; old devices stored one string.
      const fa = this.form.from_address || {};
      this.$set(this.form, "from_address", { street: fa.street || "", zip: fa.zip || "", city: fa.city || "" });

      // Nested objects the template reads without checking first.
      this.form.ava_flex = this.form.ava_flex || {};
      this.form.delivery_company = this.form.delivery_company || {};
      this.syncDeliveryCompany();
      this.form.external_contractor = this.form.external_contractor || {};
      this.form.personal_alarm = this.form.personal_alarm || {};
      ["reporting", "risk_profile", "name", "home_address", "work_address", "transport", "registration_number", "model", "color"].forEach((k) => {
        if (this.form.personal_alarm[k] === undefined) this.$set(this.form.personal_alarm, k, "");
      });

      // Always offer the three contact-person rows.
      this.form.contact_persons = mergeContactPersons(this.form.contact_persons, []);

      if (!this.form.securDesktop || !Array.isArray(this.form.securDesktop.contactPersons) || this.form.securDesktop.contactPersons.length === 0) {
        this.$set(this.form, "securDesktop", {
          alarmMessage: (this.form.securDesktop && this.form.securDesktop.alarmMessage) || "",
          endMessage: (this.form.securDesktop && this.form.securDesktop.endMessage) || "",
          contactPersons: [{ name: "", phone: "" }],
        });
      }

      // ---- the replacement-specific part ----------------------------------

      // The two identifiers of the new physical unit. The replacement inherits
      // everything from the old device EXCEPT these (PM 2026-09-22), so they
      // start empty and the form will not save without them.
      this.form.modelnumber = "";
      this.form.imei_number = "";

      // Avigilon cameras are the exception: they have no serial-number or IMEI
      // field of their own - their identifier is the camera's tag serial
      // number, which is shown lower down and is required there. Blanking
      // modelnumber for them would make the device impossible to replace,
      // because the server requires a serial number it has no field for.
      if (this.isAvigilonCamera) {
        this.form.modelnumber = device.modelnumber || "";
      }

      // A camera's "tag serial number" IS its serial number - it is the field
      // the form shows instead of modelnumber for Avigilon - so it is blanked
      // for the same reason. The webhook and the camera token are ordinary
      // settings and are inherited like everything else.
      if (this.isAvigilonCamera) {
        this.$set(this.form, "ava_flex", {
          // The new camera's own tag serial number - typed in, like a serial.
          tag_serial_number: "",
          webhook: (device.ava_flex && device.ava_flex.webhook) || "",
          camera_token: (device.ava_flex && device.ava_flex.camera_token) || "",
        });
      }

      // This is an exchange unit (PM 2026-09-22).
      this.form.device_status = "utbytesenhet";

      // No delivery document carried over from the previous device's form.
      this.stock_order_id = "";

      // Everything else is inherited, including the alarm/object number, the
      // inventory number, the telematics number and the contract number. Those
      // last three are identifiers, so they are cleared from the old device
      // when this form is saved - they belong to one device at a time.
      //
      // The IMEI is the exception that is cleared on BOTH sides: the new
      // hardware carries its own, and clearing the old one is what stops
      // reports being matched to the retired record.

      this.check_personal_alarm();
      this.check_alarm_center_specifics();

      if (this.$refs.formReplaceDevice) this.$refs.formReplaceDevice.reset();
    },

    // The customer and department lists for the "where does the old device go"
    // pickers. Loaded once when the form is created.
    getCustomersAndUnits() {
      Promise.all([
        this.axios.get(`${process.env.VUE_APP_SERVER_URL}/customer/`),
        this.axios.get(`${process.env.VUE_APP_SERVER_URL}/units/`),
      ])
        .then(([customerResponse, unitResponse]) => {
          this.customers = (customerResponse.data && customerResponse.data.data) || [];
          this.all_units = (unitResponse.data && unitResponse.data.data) || [];
          this.preselectRmaDepartment();
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    // Preselect where the replaced device goes.
    //
    // The customer comes first and is found by name, so the same build works on
    // production and on staging. The department is then chosen from the
    // device's manufacturer: an SRT device suggests "RMA SRT", a Twig device
    // "RMA TWIG", and so on.
    //
    // If the manufacturer is unknown, or its department has not been created
    // yet, the department is deliberately left empty for the admin to pick. It
    // is a required field, so nothing can be saved without one either way.
    preselectRmaDepartment() {
      this.rma_customer_id = "";
      this.rma_customer_units = [];
      this.rma_unit_id = "";

      // 1. the customer that holds the RMA departments
      let customer = null;
      for (const name of DEFAULT_RMA_CUSTOMER_NAMES) {
        customer = this.customers.find((c) => (c.name || "").trim().toLowerCase() === name.toLowerCase());
        if (customer) break;
      }
      if (!customer) return;

      this.rma_customer_id = customer._id;
      this.rma_customer_units = this.all_units.filter((u) => u.parent_id === customer._id);

      // 2. the department for this device's manufacturer
      const wanted = this.rmaDepartmentName;
      if (!wanted) return;

      const department = this.rma_customer_units.find(
        (u) => (u.name || "").trim().toLowerCase() === wanted.toLowerCase()
      );
      if (department) this.rma_unit_id = department.unit_id;
    },

    // The admin picked a different customer: show its departments and make
    // them choose one, rather than silently keeping the previous department.
    onRmaCustomerChange() {
      this.rma_customer_units = this.all_units.filter((u) => u.parent_id === this.rma_customer_id);
      const stillThere = this.rma_customer_units.some((u) => u.unit_id === this.rma_unit_id);
      if (!stillThere) this.rma_unit_id = "";
    },

    // Stock delivery documents, loaded once like on the create page.
    getStockOrders() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/orders`)
        .then((response) => {
          this.orders = (response.data && response.data.orders) || [];
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    // A delivery document picked: take the new hardware's type, serial and
    // IMEI from it, as the create page does. Clearing the choice empties the
    // two identifiers again, which is where a replacement starts.
    populateFromStockOrder() {
      if (!this.form) return;
      const order = this.orders.find((o) => o._id === this.stock_order_id);
      if (order) {
        this.form.type = order.type;
        this.form.modelnumber = order.modelnumber || "";
        this.form.imei_number = order.imei_number || "";
      } else {
        this.form.modelnumber = "";
        this.form.imei_number = "";
      }
    },

    // A different device type: a delivery document for the old type no longer
    // applies (same as filterOrders on the create page).
    onTypeChange() {
      const order = this.orders.find((o) => o._id === this.stock_order_id);
      if (order && order.type !== this.form.type) this.stock_order_id = "";
    },

    // Which alarm central is Safe-4, so the form can tell whether this device
    // is a Safe-4 device. Same call the ordinary action report makes.
    getSafe4CenterId() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/safe4/config`)
        .then((response) => {
          this.safe4_center_id = (response.data && response.data.safe4_alarm_center_id) || "";
        })
        .catch(() => {
          // Safe-4 being unavailable must not stop a replacement. Without the
          // id the form simply treats the device as not-Safe-4.
          this.safe4_center_id = "";
        });
    },

    // Step 1 of saving: check the form, then ask for the case number. Nothing
    // has been sent at this point, so backing out of the dialog changes nothing.
    askCaseNumber(sendToAlarmCenter) {
      if (this.isLoading) return;

      return this.$refs.formReplaceDevice.validate().then((success) => {
        if (!success) {
          this.handleError(null, this.$t("change_alarm_plan.fields_are_empty"));
          return;
        }

        this.sendToAlarmCenter = !!sendToAlarmCenter;
        this.caseNumber = "";
        this.caseNumberError = false;
        this.askingCaseNumber = true;
      });
    },

    // Step 2: the case number is mandatory (PM 2026-09-22), so an empty one
    // keeps the dialog open instead of saving without it.
    confirmCaseNumber() {
      if (this.isLoading) return;

      if (!this.caseNumber) {
        this.caseNumberError = true;
        return;
      }

      this.caseNumberError = false;
      this.submit();
    },

    // Backing out of the confirmation. Nothing has been sent, so both devices
    // are untouched and the form is left exactly as it was.
    cancelCaseNumber() {
      this.resetConfirmation();
    },

    // Put the confirmation back to its starting state.
    //
    // Bound to the modal's @before-close as well, because Cancel is not the
    // only way out: Esc closes the window through vue-js-modal without going
    // anywhere near our own methods. Without this, an Esc while the dialog is
    // open would leave it armed - and it would reappear over the NEXT device's
    // form, still holding the previous case number and still set to email the
    // alarm centre.
    resetConfirmation() {
      this.askingCaseNumber = false;
      this.sendToAlarmCenter = false;
      this.caseNumber = "";
      this.caseNumberError = false;
    },

    // Save. This is the point at which the replacement actually happens: the
    // server creates the new device and retires the old one in one request.
    submit() {
      // Set before validating, not after: validate() is async, and two clicks
      // inside that gap would otherwise send two replacements and create two
      // live devices sharing an IMEI.
      if (this.isLoading) return;

      // Guard the ref before the flag, so a missing observer cannot leave the
      // form stuck with every button disabled.
      if (!this.$refs.formReplaceDevice) return;
      this.isLoading = true;

      return this.$refs.formReplaceDevice.validate().then((success) => {
        if (!success) {
          this.isLoading = false;
          // Close the confirmation too, or the overlay keeps covering the very
          // fields the message is complaining about.
          this.resetConfirmation();
          this.handleError(null, this.$t("change_alarm_plan.fields_are_empty"));
          return;
        }

        let validContacts = (this.form.securDesktop && this.form.securDesktop.contactPersons)
          ? this.form.securDesktop.contactPersons.filter((contact) => {
              return contact.name && contact.phone && (contact.name.trim() !== "" || contact.phone.trim() !== "");
            })
          : [];

        // AJAX units have no IMEI of their own - the serial number is used as
        // the IMEI. Same rule the create page and the action report apply.
        let imeiNumber = this.form.imei_number;
        if (/^AJAX/.test(this.form.type)) imeiNumber = this.form.modelnumber;

        // Safe-4 keeps these fields in upper case (SEC-364)
        if (this.isSafe4Selected) this.applySafe4UpperCase();

        let data = {
          type: this.form.type,
          imei_number: imeiNumber,
          phone: this.form.phone,
          imsi_number: this.showImsiNumber ? (this.form.imsi_number || "") : "", // SEC-337
          modelnumber: this.form.modelnumber,
          operator: this.form.operator,
          alarm_central: this.form.alarm_central,
          alarm_number: this.form.alarm_number,
          man_down: this.form.man_down,
          direct_dispatch: this.form.direct_dispatch,
          cash_agreement: this.form.cash_agreement,
          agreement_number: this.form.agreement_number,
          agreement_length: this.form.agreement_length,
          seller: this.form.seller,
          contract_party: this.form.contract_party || "",
          delivery_company: this.form.delivery_company,
          selected_alarm_sort: this.form.selected_alarm_sort,
          personal_alarm: this.form.personal_alarm,
          risk_profile: this.form.risk_profile,
          reference_number: this.form.reference_number,
          user: this.form.user,
          unit_name: this.form.unit_name,
          from_address: this.form.from_address,
          alarm_unit_name: this.form.alarm_unit_name,
          business_description: this.form.business_description,
          alarm_password: this.form.alarm_password,
          personal_password: this.form.personal_password,
          external_contractor: this.form.external_contractor,
          contact_persons: this.form.contact_persons,
          comment: this.form.comment,
          contract_start: this.form.contract_start,
          ava_flex: {
            tag_serial_number: this.form.ava_flex.tag_serial_number,
            webhook: this.form.ava_flex.webhook,
            camera_token: this.form.ava_flex.camera_token,
          },
          inventory_number: this.form.inventory_number,
          securDesktop: {
            alarmMessage: this.form.securDesktop.alarmMessage,
            endMessage: this.form.securDesktop.endMessage,
            contactPersons: validContacts,
          },
          device_status: this.form.device_status || "utbytesenhet",
          // Where the REPLACED device is sent. Chosen above; defaults to the
          // RMA department for this device's manufacturer.
          rma_unit_id: this.rma_unit_id,
          // The RMA case number. Stored only on the retired device, as part of
          // its new name - never on the replacement.
          case_number: this.caseNumber,
          // The stock delivery document used, consumed by the server once the
          // replacement is done (as on the create page).
          order_id: this.stock_order_id || "",
        };

        return this.axios
          .post(`${process.env.VUE_APP_SERVER_URL}/alarms/${this.alarmInfo._id}/replace`, data)
          .then(async (response) => {
            this.askingCaseNumber = false;

            // Nothing to report about Safe-4 or Westra here. Both devices are
            // already saved by the time this answer arrives, and the alarm
            // centrals are updated in the background so a slow or unreachable
            // one can never hold up, or appear to fail, the replacement. A
            // problem out there is written to the server log for support.
            this.handleSuccess(this.$t("replace_device.replaced"));

            // "Spara, byt och skicka till larmcentral": send the updated action
            // report on to the connected centre. The replacement itself has
            // already succeeded, so a failure here is reported but never
            // presented as a failed replacement.
            if (this.sendToAlarmCenter) {
              const newAlarmId = (response.data && response.data.alarm_id) || null;
              await this.sendReplacementToAlarmCenter(newAlarmId, data);
            }

            // The delivery document is consumed server-side
            if (this.stock_order_id) {
              this.orders = this.orders.filter((o) => o._id !== this.stock_order_id);
              this.stock_order_id = "";
            }

            this.isLoading = false;
            this.$emit("deviceReplaced");

            // Close the replace form and the two device windows behind it: the
            // device they were showing has just moved to RMA.
            this.$modal.hide("replace-device");
            this.$modal.hide("update-alarm-contract");
            this.$modal.hide("update-alarm");
          })
          .catch((error) => {
            this.isLoading = false;
            this.askingCaseNumber = false;

            // A serial number, IMEI, telematics number or inventory number
            // already used by another device (SEC-216).
            if (this.handleIdentifierConflict && this.handleIdentifierConflict(error)) return;

            // Safe-4 refused the replacement, so both devices are untouched
            // (SEC-364 B1, C2). The fields it complained about are marked.
            if (this.handleSafe4Rejection(error, this.$refs.formReplaceDevice)) return;

            const body = error.response ? error.response.data : null;

            // The RMA department is not set up. Nothing was saved.
            if (error.response && error.response.status === 422 && body && body.error_code === "rma_unit_not_found") {
              this.handleError(null, this.$t("replace_device.rma_unit_missing"));
              return;
            }

            // This device has already been replaced once and is sitting in RMA.
            if (body && body.error_code === "already_replaced") {
              this.handleError(null, this.$t("replace_device.already_replaced"));
              return;
            }

            // The form was rejected by the server's own validation. Without
            // this the admin gets a bare "something went wrong" and no idea
            // which field is at fault.
            if (error.response && error.response.status === 422) {
              this.handleError(null, this.$t("change_alarm_plan.fields_are_empty"));
              return;
            }

            // The replacement could not be completed. Both devices are unchanged.
            if (body && (body.error_code === "create_failed" || body.error_code === "retire_failed")) {
              this.handleError(null, this.$t("replace_device.replace_failed"));
              return;
            }

            this.handleError(error);
          });
      });
    },

    // Send the updated action report to the connected alarm receiving centre.
    //
    // Built the same way the ordinary action-report form builds it, with one
    // difference: highlight is ON and the replaced device is passed as the
    // "old" one, so every value that changed is shaded in the document and the
    // IMEI turns up in its "Changes summary" table with its old and new value.
    // That is what makes the swap obvious to whoever reads it.
    //
    // Called only after the replacement has already succeeded, so a failure
    // here is reported on its own and never as a failed replacement.
    async sendReplacementToAlarmCenter(newAlarmId, sentData) {
      // Nothing to send, or nowhere to send it to.
      if (!this.canSendToAlarmCenter) {
        this.handleError(null, this.$t("replace_device.no_alarm_center"));
        return;
      }
      const alarmCentral = this.form.alarm_central;

      try {
        const customerName = this.customer ? this.customer.name : "";

        // The new device as it was just saved, and the device it replaced.
        const newAlarm = { ...sentData, _id: newAlarmId };

        const doc = await this.generateAlarmPlanPDF({
          alarm: newAlarm,
          customerName: customerName,
          sellers: this.sellers,
          oldAlarm: this.alarmInfo,
          highlight: true,
          showAlarmNumber: this.show_alarm_number,
          showImsiNumber: this.showImsiNumber, // SEC-337
          showDirectDispatch: this.showDirectDispatch,
          unit: this.all_units.find((u) => u.unit_id === (newAlarm.parent_id || this.alarmInfo.parent_id)) || null,
          customer: this.customer,
        });

        const headers = {
          "X-Access-Token": this.$store.state.token,
          "Content-Type": "multipart/form-data",
        };

        const formData = new FormData();
        formData.append("pdf", doc.output("blob"));
        formData.append("alarm_id", newAlarmId);
        // Only a fallback: the server works the email subject out from its own
        // send log, and a brand new device is not in it, so the centre gets
        // "Nytt larmunderlag" either way. Which is right for a swapped unit -
        // this IMEI has never been registered with them before.
        formData.append("is_new", "false");

        const response = await this.axios.post(
          `${process.env.VUE_APP_SERVER_URL}/alarm-center/${alarmCentral}/email`,
          formData,
          headers
        );

        if (response.data && response.data.error) {
          this.handleError(null, this.$t("change_alarm_plan.email_send_error"));
        } else {
          this.handleSuccess(this.$t("change_alarm_plan.sent_to_alarm_center"));
        }
      } catch (error) {
        // The replacement stands; only the message out did not go.
        this.handleError(error, this.$t("change_alarm_plan.email_send_error"));
      }
    },

    // Cancel. Nothing has been sent, so both devices are untouched.
    hide() {
      if (this.$refs.formReplaceDevice) this.$refs.formReplaceDevice.reset();
      this.$modal.hide("replace-device");
    },
    // SEC-321: default Avtalspart to CSG when the user makes the field visible
    // (Westra + CSG seller), and clear it whenever that combination is broken.
    // Bound to @change (user actions only) so loading a saved alarm never
    // overrides its stored value.
    check_contract_party() {
      if (!this.form) return;
      if (this.showContractParty) {
        if (!this.form.contract_party) this.form.contract_party = 'Commuter Security Group AB (CSG)';
      } else {
        this.form.contract_party = '';
      }
    },
    check_personal_alarm() {
      if (this.form && normalizeAlarmSort(this.form.selected_alarm_sort) === ALARM_SORT.PERSONAL) this.show_personal_alarm = true;
      else this.show_personal_alarm = false;
    },
    // SEC-321: when the alarm central changes, run the existing specifics check
    // and then re-evaluate the Avtalspart field. Wrapped so the existing method
    // stays untouched.
    onAlarmCentralChange() {
      this.check_alarm_center_specifics();
      this.check_contract_party();
      // SEC-337: the IMSI belongs to Securitas Sverige AB only (user action
      // only, so opening the form never wipes the inherited value)
      if (this.form && !this.showImsiNumber) this.form.imsi_number = "";
    },
    regenerateAlarmNumberFromSerial() {
      // Force regenerate alarm number from serial number (modelnumber)
      if (this.form && this.form.modelnumber) {
        const last6Digits = this.form.modelnumber.slice(-6);
        this.form.alarm_number = '8' + last6Digits;
      }
    },
    switchAgreementType() {
      if (this.form.cash_agreement) {
        this.form.agreement_number = "Kontant";
        this.form.agreement_length = "-";
      } else if (!this.form.cash_agreement && isAgreementNumber(this.alarmInfo.agreement_number)) {
        this.form.agreement_number = this.alarmInfo.agreement_number;
        this.form.agreement_length = this.alarmInfo.agreement_length;
      } else {
        this.form.agreement_number = "";
        this.form.agreement_length = "";
      }
    },
    check_alarm_center_specifics() {
      if (this.form) {
        let a = _.find(this.alarmCentrals, { _id: this.form.alarm_central });

        //Show alarm number field
        if (a && /(Stanley Security)|(Securitas AB)|(Rapid Larmcentral)|(Cubsec Larmcentral)|(Security Assistance Syd AB)|(Addici Security AB)|(Avarn HTTP Test)|(Avarn Security AS)|(NO - alarm central)/.test(a.name)) {
          this.show_alarm_number = true;
          // For Norwegian site, auto-generate alarm number from serial number
          this.generateAlarmNumberForNorway();
        }
          else {
            this.form.alarm_number = "";
            this.show_alarm_number = false;
          }
          // Show alarm center specific risk profiles
          if (a && /(Malmö Stads Larmcentral)|(Skyresponse Malmö)/.test(a.name)) {
            this.show_MSL_risk = true;
          } else {
            this.show_MSL_risk = false;
          }
      }
    },
    generateAlarmNumberForNorway() {
      // Only for Norwegian site and when alarm number field is shown
      // Only auto-generate if alarm_number is empty (preserve existing saved values from database)
      if (this.$options.siteContext.isNorwegianSite() && this.show_alarm_number && this.form && this.form.modelnumber && !this.form.alarm_number) {
        // Get last 6 digits of serial number (modelnumber) and prepend with 8
        const last6Digits = this.form.modelnumber.slice(-6);
        this.form.alarm_number = '8' + last6Digits;
      }
    },
  },
};
</script>
